import React from 'react'
import isEmpty from 'lodash-es/isEmpty'
import { X } from 'lucide-react'

import { useFilter } from '@rewardops/ui'

import { handleEnterKeyPress } from '~/utils/event-handler-utils'
import { buildPriceRangeTagMessage } from '~/components/maple/filters/utils'
import { styling } from '~/components/styling'
import { useTranslation } from '~/app/i18n/client'

interface TagsPickerProps {
  brandOptions?: { key: string; name: string }[]
  categoryOptions?: { key: string; name: string }[]
  earnRateOptions?: { key: string; name: string }[]
}

export const TagsPicker = ({
  brandOptions,
  categoryOptions,
  earnRateOptions,
}: TagsPickerProps) => {
  const { t } = useTranslation()

  const {
    selectedBrands,
    selectedCategories,
    selectedEarnRates,
    minPrice,
    maxPrice,
    onlyItemsWithinBalance,
    addToSelectedBrand,
    addToSelectedCategory,
    addToSelectedEarnRates,
    setMinMaxPrice,
    setOnlyItemsWithinBalance,
    isEarnRateSelected,
    clearFilters,
  } = useFilter()

  const isFilterNotApplied =
    isEmpty(selectedBrands) &&
    isEmpty(selectedCategories) &&
    isEmpty(selectedEarnRates) &&
    minPrice < 1 &&
    maxPrice < 1 &&
    !onlyItemsWithinBalance

  if (isFilterNotApplied) {
    return null
  }

  const filteredBrandOptions =
    brandOptions?.filter((option: any) =>
      selectedBrands.includes(option.key),
    ) || []

  const filteredCategoryOptions =
    categoryOptions?.filter((option: any) =>
      selectedCategories.includes(option.key),
    ) || []

  const filteredEarnRateOptions =
    earnRateOptions?.filter((option: any) => isEarnRateSelected(option.key)) ||
    []

  return (
    <div className="flex flex-wrap items-center gap-2 md:pb-1 lg:pb-5">
      {filteredBrandOptions.map((option) => (
        <Tag
          id={`brand-tag-${option.key}`}
          key={option.key}
          name={option.name}
          onClick={() => addToSelectedBrand(option)}
        />
      ))}
      {filteredCategoryOptions.map((option) => (
        <Tag
          id={`category-tag-${option.key}`}
          key={option.key}
          name={option.name}
          onClick={() => addToSelectedCategory(option)}
        />
      ))}
      {filteredEarnRateOptions.map((option) => (
        <Tag
          id={`earn-rate-tag-${option.key}`}
          key={option.key}
          name={(option as any).text || (option as any).name}
          onClick={() => addToSelectedEarnRates(option)}
        />
      ))}
      <PriceTag
        minPrice={minPrice}
        maxPrice={maxPrice}
        onClick={() => {
          setMinMaxPrice({ minPrice: 0, maxPrice: 0 })
        }}
      />
      {onlyItemsWithinBalance && (
        <Tag
          id="only-items-within-balance-tag"
          key="only-items-within-balance-tag"
          name={t('filters.onlyItemsWithinBalance')}
          onClick={() => setOnlyItemsWithinBalance(false)}
        />
      )}
      <div
        id="clear-all-tags-button"
        role="button"
        tabIndex={0}
        className="cursor-pointer"
        onClick={clearFilters}
        onKeyDown={(e) => handleEnterKeyPress(e, clearFilters)}
      >
        {t('filters.clearAll')}
      </div>
    </div>
  )
}

// Have to set it as a separate component to avoid the error: "Rendered more hooks than during the previous render"
function PriceTag({
  minPrice,
  maxPrice,
  onClick,
}: {
  minPrice: number
  maxPrice: number
  onClick: () => void
}) {
  const priceRange =
    minPrice || maxPrice
      ? buildPriceRangeTagMessage(minPrice, maxPrice)
      : undefined

  return priceRange ? (
    <Tag id="price-range-tag" name={priceRange} onClick={onClick} />
  ) : null
}

function Tag({
  id,
  name,
  onClick,
}: {
  id: string
  name: string
  onClick: () => void
}) {
  return (
    <div id={id} className={styling.tag}>
      {name}{' '}
      {
        <X
          id={`${id}-remove-icon`}
          className="ml-2 cursor-pointer hover:text-slate-500"
          size={18}
          onClick={onClick}
        />
      }
    </div>
  )
}
